<template>
    <div class="top-bar-container">
        <!-- 浮动头像按钮 -->
        <div class="floating-avatar" @mouseenter="showFullTopBar = true" @click="toggleTopBar">
            <div class="avatar">
                <img :src="user.avatar" alt="用户头像" @error="handleImageError" />
            </div>
        </div>

        <!-- 完整的TopBar内容 -->
        <div class="top-bar-full" v-show="showFullTopBar" @mouseleave="hideTopBar">
            <div class="top-bar">
                <div class="title">怕什么真理无穷，进一寸有进一寸的欢喜</div>
                <div class="user-menu" @click="showUserSettings = true">
                    <div class="avatar">
                        <img :src="user.avatar" alt="用户头像" @error="handleImageError" />
                    </div>
                    <span class="username">{{ user.name }}</span>
                    <button class="logout-btn" @click="handleLogout">退出</button>
                </div>
            </div>

            <!-- 用户设置弹窗 -->
            <div v-if="showUserSettings" class="user-settings-dialog" @click="closeUserSettings">
                <div class="user-settings-content" @click.stop>
                    <h3>用户设置</h3>
                    <div class="user-info-section">
                        <div class="avatar-upload">
                            <div class="avatar-preview">
                                <img :src="localUser.avatar" alt="用户头像" @error="handleImageError" />
                            </div>
                            <button class="upload-btn" @click="triggerAvatarUpload">更换头像</button>
                            <input type="file" ref="avatarInput" accept="image/*" @change="handleAvatarUpload"
                                style="display: none;" />
                        </div>
                        <div class="user-fields">
                            <div class="form-group">
                                <label>用户名:</label>
                                <input v-model="localUser.name" type="text" placeholder="请输入用户名" />
                            </div>
                            <div class="form-group">
                                <label>电话:</label>
                                <input v-model="localUser.phone" type="text" placeholder="请输入电话号码" />
                            </div>
                        </div>
                    </div>
                    <div class="password-section">
                        <button class="change-password-btn" @click="showChangePassword = true">修改密码</button>
                    </div>
                    <div class="settings-actions">
                        <button @click="saveUserSettings">保存</button>
                        <button @click="closeUserSettings">取消</button>
                    </div>
                </div>
            </div>

            <!-- 修改密码弹窗 -->
            <div v-if="showChangePassword" class="change-password-dialog" @click="showChangePassword = false">
                <div class="change-password-content" @click.stop>
                    <h3>修改密码</h3>
                    <div class="form-group">
                        <label>原密码:</label>
                        <input v-model="passwordForm.oldPassword" type="password" placeholder="请输入原密码" />
                    </div>
                    <div class="form-group">
                        <label>新密码:</label>
                        <input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" />
                    </div>
                    <div class="form-group">
                        <label>确认新密码:</label>
                        <input v-model="passwordForm.confirmPassword" type="password" placeholder="请确认新密码" />
                    </div>
                    <div class="password-actions">
                        <button @click="changePassword">确定</button>
                        <button @click="showChangePassword = false">取消</button>
                    </div>
                </div>
            </div>

            <!-- 横向标签栏 -->
            <div class="tab-bar">
                <div class="tabs">
                    <!-- 主页标签 -->
                    <div class="tab" :class="{ active: activeTab === 'home' }" @click="switchTab('home')">
                        主页
                    </div>

                    <!-- 排行榜标签 -->
                    <div class="tab" :class="{ active: activeTab === 'ranking' }" @click="switchTab('ranking')">
                        排行榜
                    </div>

                    <!-- 用户自定义标签 -->
                    <div v-for="tab in userTabs" :key="tab.id" class="tab" :class="{ active: activeTab === tab.id }"
                        @click="navigateToApp(tab.link)">
                        {{ tab.name }}
                        <span class="remove-tab" @click.stop="removeTab(tab.id)">×</span>
                    </div>
                </div>

                <!-- 添加标签按钮 -->
                <button class="add-tab-btn" @click="showAddTabDialog = true">+</button>
            </div>

            <!-- 添加标签对话框 -->
            <div v-if="showAddTabDialog" class="add-tab-dialog">
                <div class="dialog-content">
                    <h3>添加标签</h3>
                    <div class="app-list">
                        <div v-for="app in availableApps" :key="app.id" class="app-item" @click="addTab(app)">
                            {{ app.name }}
                        </div>
                    </div>
                    <div class="dialog-actions">
                        <button @click="showAddTabDialog = false">取消</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import AuthService from '@/services/authService'
import UserService from '@/services/userService'

export default {
    name: 'TopBar',
    props: {
        user: {
            type: Object,
            required: true
        },
        apps: {
            type: Array,
            default: () => []
        }
    },
    setup(props) {
        const router = useRouter()

        // 标签相关状态
        const activeTab = ref('home')
        const showAddTabDialog = ref(false)
        const userTabs = ref([])
        const showFullTopBar = ref(false) // 控制是否显示完整TopBar

        // 用户设置相关状态
        const showUserSettings = ref(false)
        const showChangePassword = ref(false)
        const localUser = ref({
            avatar: props.user.avatar,
            avatarFile: null, // 用于存储上传的头像文件
            name: props.user.name,
            phone: props.user.phone || ''
        })
        const passwordForm = ref({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        })
        const avatarInput = ref(null)

        // 获取可用的应用列表（未被添加为标签的应用）
        const availableApps = computed(() => {
            const usedAppIds = userTabs.value.map(tab => tab.id)
            return props.apps.filter(app => !usedAppIds.includes(app.id))
        })

        // 切换标签
        const switchTab = (tabId) => {
            activeTab.value = tabId
            if (tabId === 'home') {
                router.push('/')
            } else if (tabId === 'ranking') {
                router.push('/ranking')
            }
        }

        // 导航到应用
        const navigateToApp = (link) => {
            console.log('导航到应用:', link)
            if (link === '/todo') {
                router.push('/todo')
            } else {
                window.location.href = link
            }
        }

        // 添加标签
        const addTab = (app) => {
            // 检查是否已存在
            if (!userTabs.value.find(tab => tab.id === app.id)) {
                userTabs.value.push({
                    id: app.id,
                    name: app.name,
                    link: app.link
                })
            }
            showAddTabDialog.value = false
        }

        // 移除标签
        const removeTab = (tabId) => {
            userTabs.value = userTabs.value.filter(tab => tab.id !== tabId)
        }

        // 处理头像加载错误
        const handleImageError = (event) => {
            // 当头像加载失败时，使用默认头像
            event.target.src = require('@/assets/logo.png')
        }

        // 处理登出
        const handleLogout = async () => {
            try {
                await AuthService.logout()
                // 跳转到登录页面
                router.push('/login')
            } catch (error) {
                console.error('登出失败:', error)
            }
        }

        // 从本地存储加载用户标签
        const loadUserTabs = () => {
            try {
                const savedTabs = localStorage.getItem('userTabs')
                if (savedTabs) {
                    userTabs.value = JSON.parse(savedTabs)
                }
            } catch (error) {
                console.error('加载用户标签失败:', error)
            }
        }

        // 保存用户标签到本地存储
        const saveUserTabs = () => {
            try {
                localStorage.setItem('userTabs', JSON.stringify(userTabs.value))
            } catch (error) {
                console.error('保存用户标签失败:', error)
            }
        }

        // 监听标签变化并保存到本地存储
        watch(userTabs, () => {
            saveUserTabs()
        }, { deep: true })

        // 切换TopBar显示状态
        const toggleTopBar = () => {
            showFullTopBar.value = !showFullTopBar.value;
            // if (showFullTopBar.value) {
            //     showFullTopBar.value = false;
            // }
        }

        // 隐藏TopBar
        const hideTopBar = () => {
            showFullTopBar.value = false;
        }

        // 显示用户设置弹窗
        const showUserSettingsDialog = () => {
            showUserSettings.value = true;
        }

        // 关闭用户设置弹窗
        const closeUserSettings = () => {
            showUserSettings.value = false;
            showChangePassword.value = false;
            // 重置表单
            localUser.value = {
                avatar: props.user.avatar,
                avatarFile: null, // 重置头像文件
                name: props.user.name,
                phone: props.user.phone || ''
            };
            passwordForm.value = {
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            };
        }

        // 触发头像上传
        const triggerAvatarUpload = () => {
            if (avatarInput.value) {
                avatarInput.value.click();
            }
        }

        // 处理头像上传
        const handleAvatarUpload = (event) => {
            const file = event.target.files[0];
            if (file) {
                // 保存文件对象以便后续上传
                localUser.value.avatarFile = file;

                // 预览图片
                const reader = new FileReader();
                reader.onload = (e) => {
                    localUser.value.avatar = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        }

        // 保存用户设置
        const saveUserSettings = async () => {
            try {
                // 如果有上传头像文件，使用FormData格式
                if (localUser.value.avatarFile) {
                    const formData = new FormData();
                    formData.append('nickname', localUser.value.name);
                    formData.append('phone', localUser.value.phone);
                    formData.append('avatar', localUser.value.avatarFile);

                    // 调用API保存用户设置（包含头像）
                    const response = await UserService.updateUserInfoWithAvatar(formData);
                    console.log('用户信息保存成功:', response);
                } else {
                    // 准备用户数据（不包含头像）
                    const userData = {
                        nickname: localUser.value.name,
                        phone: localUser.value.phone
                    };

                    // 调用API保存用户设置
                    const response = await UserService.updateUserInfo(userData);
                    console.log('用户信息保存成功:', response);
                }

                // 更新父组件的用户信息
                // 注意：在实际应用中，你可能需要通过emit或其他方式通知父组件更新用户信息

                // 显示成功消息
                alert('用户信息保存成功');

                // 关闭弹窗
                closeUserSettings();
            } catch (error) {
                console.error('保存用户信息失败:', error);
                alert('保存用户信息失败，请重试');
            }
        }

        // 修改密码
        const changePassword = async () => {
            // 验证密码
            if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
                alert('新密码和确认密码不一致');
                return;
            }

            // 检查密码是否为空
            if (!passwordForm.value.oldPassword || !passwordForm.value.newPassword) {
                alert('请填写所有密码字段');
                return;
            }

            try {
                // 准备密码数据
                const passwordData = {
                    old_password: passwordForm.value.oldPassword,
                    new_password: passwordForm.value.newPassword
                };

                // 调用API修改密码
                // 注意：这里需要一个专门的修改密码API端点
                // 由于UserService中没有专门的修改密码方法，我们暂时使用updateUserInfo
                // 在实际应用中，应该有一个专门的修改密码端点
                const response = await UserService.updateUserInfo(passwordData);
                console.log('密码修改成功:', response);

                // 显示成功消息
                alert('密码修改成功');

                // 关闭修改密码弹窗
                showChangePassword.value = false;

                // 重置密码表单
                passwordForm.value = {
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                };
            } catch (error) {
                console.error('密码修改失败:', error);
                // 检查是否是密码错误
                if (error.response && error.response.status === 400) {
                    alert('原密码错误，请重新输入');
                } else {
                    alert('密码修改失败，请重试');
                }
            }
        }

        onMounted(() => {
            loadUserTabs()
        })

        return {
            activeTab,
            showAddTabDialog,
            userTabs,
            availableApps,
            switchTab,
            navigateToApp,
            addTab,
            removeTab,
            handleImageError,
            handleLogout,
            showFullTopBar, // 返回响应式变量
            toggleTopBar,
            hideTopBar,

            // 用户设置相关
            showUserSettings,
            showChangePassword,
            localUser,
            passwordForm,
            avatarInput,
            showUserSettingsDialog,
            closeUserSettings,
            triggerAvatarUpload,
            handleAvatarUpload,
            saveUserSettings,
            changePassword
        }
    }
}
</script>

<style scoped>
.top-bar-container {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 1000;
}

/* 浮动头像按钮 */
.floating-avatar {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 1001;
    cursor: pointer;
}

.floating-avatar .avatar img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

/* 完整的TopBar内容 */
.top-bar-full {
    position: absolute;
    top: 0;
    right: 0;
    width: 400px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 10px;
}

.top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    background-color: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 10px;
}

.title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
}

.user-menu {
    display: flex;
    align-items: center;
    gap: 10px;
}

.avatar img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
}

.username {
    font-weight: 500;
}

.logout-btn {
    padding: 5px 10px;
    background-color: #ff6b6b;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

/* 标签栏样式 */
.tab-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    border-radius: 8px;
    padding: 10px 15px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tabs {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    flex: 1;
}

.tab {
    padding: 8px 16px;
    background: #f0f0f0;
    border-radius: 20px;
    cursor: pointer;
    white-space: nowrap;
    position: relative;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
}

.tab:hover {
    background: #e0e0e0;
}

.tab.active {
    background: #42b983;
    color: white;
}

.remove-tab {
    margin-left: 5px;
    cursor: pointer;
    font-weight: bold;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.remove-tab:hover {
    background: rgba(255, 255, 255, 0.3);
}

.add-tab-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #42b983;
    color: white;
    border: none;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.add-tab-btn:hover {
    background: #359c6d;
}

/* 添加标签对话框样式 */
.add-tab-dialog {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.dialog-content {
    background: white;
    border-radius: 12px;
    padding: 20px;
    width: 300px;
    max-height: 80vh;
    overflow-y: auto;
}

.dialog-content h3 {
    margin: 0 0 15px 0;
    text-align: center;
}

.app-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
    max-height: 300px;
    overflow-y: auto;
}

.app-item {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.app-item:hover {
    background: #f0f0f0;
    border-color: #42b983;
}

.dialog-actions {
    display: flex;
    justify-content: center;
}

.dialog-actions button {
    padding: 8px 20px;
    background: #f0f0f0;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.dialog-actions button:hover {
    background: #e0e0e0;
}

/* 用户设置弹窗样式 */
.user-settings-dialog,
.change-password-dialog {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.user-settings-content,
.change-password-content {
    background: white;
    border-radius: 12px;
    padding: 20px;
    width: 400px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.user-settings-content h3,
.change-password-content h3 {
    margin: 0 0 20px 0;
    text-align: center;
    color: #333;
}

.user-info-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.avatar-upload {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.avatar-preview {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #eee;
}

.avatar-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.upload-btn {
    padding: 8px 16px;
    background: #42b983;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.upload-btn:hover {
    background: #359c6d;
}

.user-fields {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.form-group label {
    font-weight: 500;
    color: #555;
}

.form-group input {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.form-group input:focus {
    outline: none;
    border-color: #42b983;
}

.password-section {
    margin: 10px 0;
    text-align: center;
}

.change-password-btn {
    padding: 8px 16px;
    background: #f0f0f0;
    color: #333;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.change-password-btn:hover {
    background: #e0e0e0;
}

.settings-actions,
.password-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
}

.settings-actions button,
.password-actions button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
}

.settings-actions button:first-child,
.password-actions button:first-child {
    background: #42b983;
    color: white;
}

.settings-actions button:first-child:hover,
.password-actions button:first-child:hover {
    background: #359c6d;
}

.settings-actions button:last-child,
.password-actions button:last-child {
    background: #f0f0f0;
    color: #333;
}

.settings-actions button:last-child:hover,
.password-actions button:last-child:hover {
    background: #e0e0e0;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .top-bar-full {
        width: 300px;
    }

    .tabs {
        gap: 5px;
    }

    .tab {
        padding: 6px 12px;
        font-size: 14px;
    }

    .user-settings-content,
    .change-password-content {
        width: 300px;
    }
}
</style>